import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch } from 'react-redux';
import { performOfferAction } from '../../actions';
import discountsStrings from '../../strings';
import type { DiscountActionType, DiscountItem } from '../../types/discount.types';
import { toApiDiscountTypeString, toInputDate } from '../../utils/discountHelpers';

const formSchema = z.object({
  originalPrice: z.number(),
  discountValue: z.string().min(1, discountsStrings.validationRequired),
  discountType: z.enum(['percent', 'amount']),
  startDate: z.string().min(1, discountsStrings.validationRequired),
  endDate: z.string().min(1, discountsStrings.validationRequired),
}).superRefine((data, ctx) => {
  const value = Number(data.discountValue);
  if (value <= 0) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationNegativeDiscount, path: ['discountValue'] });
  }
  if (data.discountType === 'percent' && value > 100) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationPercentMax, path: ['discountValue'] });
  }
  if (data.discountType === 'amount' && value > data.originalPrice) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationAmountExceedsPrice, path: ['discountValue'] });
  }
  if (data.startDate && data.endDate && data.endDate <= data.startDate) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationEndDateAfterStart, path: ['endDate'] });
  }
});

export type EditOfferFormValues = z.infer<typeof formSchema>;

interface UseEditOfferFormOptions {
  isOpen: boolean;
  discount: DiscountItem | null;
  actionType: DiscountActionType;
}

export function useEditOfferForm({ isOpen, discount, actionType }: UseEditOfferFormOptions) {
  const dispatch = useDispatch();
  const isActive = discount?.status === 'Active';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditOfferFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      originalPrice: 0,
      discountValue: '',
      discountType: 'percent',
      startDate: '',
      endDate: '',
    },
  });

  useEffect(() => {
    if (!discount || !isOpen) return;
    reset({
      originalPrice: discount.originalPrice ?? 0,
      discountValue: String(discount.discountValue ?? discount.discountPercent ?? ''),
      discountType: discount.discountType ?? 'percent',
      startDate: toInputDate(discount.startDate),
      endDate: toInputDate(discount.endDate),
    });
  }, [discount, isOpen, reset]);

  const handleFormSubmit = handleSubmit(data => {
    if (!discount) return;
    if (isActive && data.startDate !== toInputDate(discount.startDate)) return;
    dispatch(
      performOfferAction({
        offerKey: discount.id,
        actionType,
        discountType: toApiDiscountTypeString(data.discountType),
        discountValue: Number(data.discountValue),
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
      }),
    );
  });

  return {
    register,
    errors,
    handleFormSubmit,
  };
}
